import { useFeatureFlagsContext } from '../../../FeatureFlags';
import { useClientContext } from '../../../ClientContext';
import { useScorecard, getIsBeachMode } from '../../../Scorecard';
import { useTargetScoreRangesForElements } from '../../hooks';
import { ScoreTileWrapper, ScoreTileWrapperElementSlot } from './ScoreTileWrapper';
import { useBtDetails } from '../../../BtDetails';
import { clientHasBusinesses } from '../../../FirmClient';

export type ElementRoutes = {
  Bt: string;
  Lt: string;
  Qt: string;
  Rt: string;
  Tt: string;
  Sr: string;
  Dr: string;
  Tr: string;
  Br: string;
  Er: string;
  Ir: string;
  Es?: string;
};

export function ScorecardGrid(props: {
  elementSlot: ScoreTileWrapperElementSlot;
  elementSlot1: ScoreTileWrapperElementSlot;
  elementSlotEs?: ScoreTileWrapperElementSlot;
  elementSlot2: ScoreTileWrapperElementSlot;
  elementSlot3: ScoreTileWrapperElementSlot;
  elementSlot4: ScoreTileWrapperElementSlot;
  elementSlot5: ScoreTileWrapperElementSlot;
  elementSlot6: ScoreTileWrapperElementSlot;
  elementSlot7: ScoreTileWrapperElementSlot;
  elementSlot8: ScoreTileWrapperElementSlot;
  elementSlot9: ScoreTileWrapperElementSlot;
  elementSlot10: ScoreTileWrapperElementSlot;
  Routes: ElementRoutes;
  forceShowSkeleton?: boolean;
}) {
  const { allowEstateStatus, allowTargetScoreRanges } = useFeatureFlagsContext();
  const { householdID, clientID, firmID } = useClientContext();
  const { scorecard, scorecardQuery } = useScorecard({ householdID });
  const { btDetails } = useBtDetails({ householdID, includeBusinesses: true, includePrivateStock: true });
  const { ir, er, es, sr, br, dr, tr, lt, qt, rt, bt, tt } = useTargetScoreRangesForElements({ firmID, clientID });
  const beachMode = getIsBeachMode(scorecard);
  const btIsNull = !clientHasBusinesses(btDetails?.businesses, btDetails?.privateStock);
  const estateStatusRoute = props.Routes.Es;
  const estateStatusSlot = props.elementSlotEs;
  const showEstateStatus = allowEstateStatus === true && estateStatusRoute && estateStatusSlot;

  const showSkeleton = !scorecard || scorecardQuery.isLoading || props.forceShowSkeleton;

  return (
    <div className={'flex flex-col justify-start items-start gap-1.5 sm:gap-4'}>
      <div className={'flex flex-row justify-start gap-1.5 sm:gap-4 flex-nowrap'}>
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Er/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Er'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.er}
            elementSlot={props.elementSlot}
            navigateTo={props.Routes.Er}
            min={er.min}
            max={er.max}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Ir/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Ir'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.ir}
            elementSlot={props.elementSlot1}
            navigateTo={props.Routes.Ir}
            min={ir.min}
            max={ir.max}
          />
        )}
        {showEstateStatus ? (
          showSkeleton ? (
            <div className="animate-pulse bg-elements-Es/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
          ) : (
            <ScoreTileWrapper
              scorecard={scorecard}
              elementSymbol={'Es'}
              showRange={allowTargetScoreRanges}
              score={scorecard?.es}
              elementSlot={estateStatusSlot}
              navigateTo={estateStatusRoute}
              min={es.min}
              max={es.max}
            />
          )
        ) : null}
      </div>
      <div className={'flex flex-row justify-start gap-1.5 sm:gap-4 flex-nowrap'}>
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Sr/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Sr'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.sr}
            elementSlot={props.elementSlot2}
            navigateTo={props.Routes.Sr}
            min={sr.min}
            max={sr.max}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Br/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Br'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.br}
            elementSlot={props.elementSlot3}
            navigateTo={props.Routes.Br}
            min={br.min}
            max={br.max}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Dr/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Dr'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.dr}
            elementSlot={props.elementSlot4}
            navigateTo={props.Routes.Dr}
            min={dr.min}
            max={dr.max}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Tr/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Tr'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.tr}
            elementSlot={props.elementSlot5}
            navigateTo={props.Routes.Tr}
            min={tr.min}
            max={tr.max}
          />
        )}
      </div>
      <div className={'flex flex-row justify-start gap-1.5 sm:gap-4 flex-nowrap'}>
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Lt/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Lt'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.lt}
            elementSlot={props.elementSlot6}
            navigateTo={props.Routes.Lt}
            min={lt.min}
            max={lt.max}
            beachMode={beachMode}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Qt/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Qt'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.qt}
            elementSlot={props.elementSlot7}
            navigateTo={props.Routes.Qt}
            min={qt.min}
            max={qt.max}
            beachMode={beachMode}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Rt/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Rt'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.rt}
            elementSlot={props.elementSlot8}
            navigateTo={props.Routes.Rt}
            min={rt.min}
            max={rt.max}
            beachMode={beachMode}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Bt/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Bt'}
            showRange={allowTargetScoreRanges && !btIsNull}
            score={scorecard?.bt}
            elementSlot={props.elementSlot9}
            navigateTo={props.Routes.Bt}
            min={bt.min}
            max={bt.max}
            beachMode={beachMode}
            btIsNull={btIsNull}
          />
        )}
        {showSkeleton ? (
          <div className="animate-pulse bg-elements-Tt/20 w-16 sm:w-[216px] min-h-[51px] sm:min-h-[154px] lg:min-h-[168.5px] rounded-lg sm:rounded-[21.6px] select-none" />
        ) : (
          <ScoreTileWrapper
            scorecard={scorecard}
            elementSymbol={'Tt'}
            showRange={allowTargetScoreRanges}
            score={scorecard?.tt}
            elementSlot={props.elementSlot10}
            navigateTo={props.Routes.Tt}
            min={tt.min}
            max={tt.max}
            beachMode={beachMode}
          />
        )}
      </div>
    </div>
  );
}
